/**
 * Account update log — every tracked change to this member's account
 * (account_change_log, written by a DB trigger) merged with staff/system
 * actions from audit_log. History starts 2026-10-01; nothing older exists.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type AccountChangesResponse } from '../api/client'
import { Panel } from '../components/Panel'
import { PubBadge } from '../components/PubBadge'
import { fmtDateTime } from '../lib/format'
import {
  CATEGORY_FILTERS, filterChanges, formatActionDetails, formatChangeValue, type ChangeFilter,
} from '../lib/accountChanges'

const LIMIT = 200

export function AccountChangeLogPanel({ subscriberId }: { subscriberId: number }) {
  const [filter, setFilter] = useState<ChangeFilter>('all')
  const q = useQuery({
    queryKey: ['admin-member-changes', subscriberId],
    queryFn: () =>
      api.get<AccountChangesResponse>(`/api/admin/members/${subscriberId}/changes?limit=${LIMIT}`),
  })
  const rows = filterChanges(q.data?.entries ?? [], filter)

  return (
    <Panel label={`Account update log (latest ${LIMIT})`} bodyClassName="p-0">
      <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-ink-500">
        {CATEGORY_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`label px-2 py-1 border ${
              filter === f.key ? 'border-paper text-paper' : 'border-ink-500 text-paper-trace'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {q.isLoading ? (
        <div className="px-4 py-4 label text-paper-trace">Loading…</div>
      ) : q.isError ? (
        <div className="px-4 py-4 label text-radar-red">Could not load the log.</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-4 label text-paper-trace">
          No changes recorded{filter === 'all' ? ' (history starts 2026-10-01)' : ' in this category'}.
        </div>
      ) : (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-ink-500 text-left label text-paper-trace">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">What</th>
              <th className="px-4 py-3">Change</th>
              <th className="px-4 py-3">By</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.kind}-${r.at}-${r.field}-${i}`} className="border-b border-ink-500/40 align-top">
                <td className="px-4 py-2 font-mono whitespace-nowrap text-paper-dim">{fmtDateTime(r.at)}</td>
                <td className="px-4 py-2 text-paper-dim">
                  <div>{r.label}</div>
                  {r.publication_slug && <PubBadge slug={r.publication_slug} compact />}
                </td>
                <td className="px-4 py-2 font-mono text-[11px]">
                  {r.kind === 'change' ? (
                    <>
                      <span className="text-paper-dim">{formatChangeValue(r.field, r.old_value)}</span>
                      <span className="text-paper-trace">{' → '}</span>
                      <span className="text-paper">{formatChangeValue(r.field, r.new_value)}</span>
                    </>
                  ) : (
                    <DetailLines details={r.details} />
                  )}
                </td>
                <td className="px-4 py-2 text-paper-dim">
                  <div>{r.source_label}</div>
                  {r.actor && <div className="font-mono text-[11px] text-paper-trace">{r.actor}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

function DetailLines({ details }: { details: Record<string, unknown> }) {
  const lines = formatActionDetails(details)
  if (lines.length === 0) return <span className="text-paper-trace">—</span>
  return (
    <dl className="space-y-0.5 font-sans text-[12px]">
      {lines.map((l, i) => (
        <div key={i}>
          <dt className="inline text-paper-trace">{l.label}: </dt>
          <dd className="inline text-paper-dim">{l.value}</dd>
        </div>
      ))}
    </dl>
  )
}
